import {
	FC,
	MouseEvent,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";

import {
	CircularProgress,
	IconButton,
	IconButtonProps,
	alpha,
	useTheme,
} from "@mui/material";

export type ProcedureIconButtonSize = "small" | "medium";

export type ProcedureIconButtonProps = Omit<
	IconButtonProps,
	"size" | "color"
> & {
	size?: ProcedureIconButtonSize;
	loading?: boolean;
	disableWhileLoading?: boolean;
	/**
	 * Semantic color shortcut – maps to palette keys.
	 * Falls back to MUI `color` when you need full control.
	 */
	tone?: "primary" | "info" | "success" | "warning" | "danger" | "neutral";
};

const isPromiseLike = (value: unknown): value is PromiseLike<unknown> =>
	typeof value === "object" &&
	value !== null &&
	"then" in value &&
	typeof (value as PromiseLike<unknown>).then === "function";

/**
 * Shared icon button for the procedures module.
 *
 * - Compact, rounded-rectangle by default (not circular FABs)
 * - Slight filled/outlined feel depending on `tone`
 * - Meant for small actions (toolbars, node actions, etc.)
 */
export const ProcedureIconButton: FC<ProcedureIconButtonProps> = (props) => {
	const {
		size = "small",
		tone = "primary",
		sx,
		children,
		onClick,
		disabled,
		loading = false,
		disableWhileLoading = true,
		...rest
	} = props;
	const [internalLoading, setInternalLoading] = useState(false);
	const mountedRef = useRef(true);
	const theme = useTheme();

	useEffect(() => {
		return () => {
			mountedRef.current = false;
		};
	}, []);

	const paletteColor =
		tone === "danger"
			? theme.palette.error
			: tone === "warning"
				? theme.palette.warning
				: tone === "success"
					? theme.palette.success
					: tone === "info"
						? theme.palette.info
						: tone === "neutral"
							? theme.palette.grey
							: theme.palette.primary;

	type PaletteWithMain = { main: string };
	const safePalette: PaletteWithMain =
		paletteColor &&
		"main" in paletteColor &&
		typeof paletteColor.main === "string"
			? (paletteColor as PaletteWithMain)
			: (theme.palette.primary as PaletteWithMain);
	const baseHeight = size === "small" ? 28 : 34;
	const basePadding = size === "small" ? 0.25 : 0.5;
	const safeChildren = children ?? (
		<span aria-hidden style={{ width: 24, height: 24 }} />
	);
	const isLoading = loading || internalLoading;

	const handleClick = useCallback(
		(event: MouseEvent<HTMLButtonElement>) => {
			if (!onClick) return;
			const result = onClick(event) as unknown;
			if (isPromiseLike(result)) {
				setInternalLoading(true);
				void Promise.resolve(result)
					.catch(() => undefined)
					.finally(() => {
						if (mountedRef.current) {
							setInternalLoading(false);
						}
					});
			}
		},
		[onClick],
	);

	const spinner = useMemo(
		() => (
			<CircularProgress color="inherit" size={size === "small" ? 14 : 16} />
		),
		[size],
	);

	return (
		<IconButton
			{...rest}
			onClick={handleClick}
			disabled={disabled || (disableWhileLoading && isLoading)}
			aria-busy={isLoading}
			size={size}
			sx={{
				height: baseHeight,
				width: baseHeight,
				borderRadius: 2,
				border: `1px solid ${alpha(safePalette.main, 0.35)}`,
				backgroundColor: alpha(safePalette.main, 0.04),
				color:
					tone === "neutral" ? theme.palette.text.secondary : safePalette.main,
				padding: basePadding,
				transition: "background-color 0.15s ease, border-color 0.15s ease",
				"&:hover": {
					backgroundColor: alpha(safePalette.main, 0.12),
					borderColor: safePalette.main,
				},
				"&.Mui-disabled": {
					borderColor: alpha(theme.palette.action.disabled, 0.4),
					backgroundColor: alpha(theme.palette.action.disabledBackground, 0.4),
				},
				...sx,
			}}
		>
			{isLoading ? spinner : safeChildren}
		</IconButton>
	);
};
